import { UserCheck2, UserX } from "lucide-react";
import { tv } from "tailwind-variants";

import { useMultiStepFormStore } from "@/stores";
import { formatDate, PATIENT_STATUS } from "@/utils";
import type { PatientCaseStatus } from "@/utils";

export const endScreenCaseVariants = tv({
  slots: {
    mainDiv:
      "backdrop-blur-lg from-white bg-gradient-to-b flex h-full flex-col items-center justify-center gap-10",
    externalCircle: "absolute size-24 rounded-full",
    internalCircle: "absolute size-20 rounded-full",
    icon: "z-10 rounded-full",
    statusPill: "rounded-full px-3 py-1 capitalize",
    messageBox: "rounded-xl p-4 border border",
    titleVariant: "text-2xl font-bold",
  },
  variants: {
    status: {
      [PATIENT_STATUS.ACTIVE]: {
        mainDiv: "to-success-50",
        externalCircle: "bg-success-100",
        internalCircle: "bg-success-200",
        icon: "bg-success-200 text-success-900",
        statusPill: "text-success-950 bg-success-50",
        messageBox: "bg-success-50 text-success-900",
        titleVariant: "text-success-950",
      },
      [PATIENT_STATUS.DISQUALIFIED]: {
        mainDiv: "to-error-50",
        externalCircle: "bg-error-50",
        internalCircle: "bg-error-100",
        icon: "bg-error-100 text-error-700",
        statusPill: "text-error-700 bg-error-100",
        messageBox: "bg-error-50 text-error-950 border-error-700",
        titleVariant: "text-error-950",
      },
      [PATIENT_STATUS.TRANSFERRED]: {
        mainDiv: "to-warning-50",
        externalCircle: "bg-warning-50",
        internalCircle: "bg-warning-100",
        icon: "bg-warning-100 text-warning-900",
        statusPill: "text-warning-950 bg-warning-50",
        messageBox: "bg-warning-50 text-warning-950 border-warning-600",
        titleVariant: "text-warning-950",
      },
    },
  },
  defaultVariants: {
    status: PATIENT_STATUS.ACTIVE,
  },
});

interface EndScreenCaseProps {
  status: Exclude<PatientCaseStatus, "new_patient">;
  description: string;
  title: string;
  hasDme?: boolean;
}

export const EndScreenCase = ({
  status,
  description,
  title,
  hasDme = false,
}: EndScreenCaseProps) => {
  const { multiStepFormData } = useMultiStepFormStore();
  const generalInformationFormData =
    multiStepFormData?.generalInformationFormData;

  const { externalCircle, internalCircle, icon, mainDiv, titleVariant } =
    endScreenCaseVariants({
      status,
    });

  if (!generalInformationFormData) {
    return null;
  }
  return (
    <div className={mainDiv()}>
      <div className="relative flex items-center justify-center pb-6">
        <div className={externalCircle()}></div>
        <div className={internalCircle()}></div>
        {!hasDme && status === "transferred" ? (
          <UserX className={icon()} size={30} />
        ) : (
          <UserCheck2 className={icon()} size={30} />
        )}
      </div>
      <div className="flex flex-col gap-y-3 text-center">
        <h3 className={titleVariant()}>{title}</h3>
        <p className="text-primary-950">{description}</p>
      </div>
      {status === PATIENT_STATUS.TRANSFERRED && hasDme && (
        <div className="flex w-3/5 justify-between rounded-xl border border-neutral-50 bg-white p-4">
          <p className="content-center font-medium text-primary-950">
            {`${generalInformationFormData.firstName} ${generalInformationFormData.lastName}`}
          </p>
          <p className="content-center text-sm text-neutral-500">
            {formatDate(generalInformationFormData.dateOfBirth)}
          </p>
          <p className="content-center text-sm text-neutral-500">
            {generalInformationFormData.phoneNumber}
          </p>
          <p className="content-center text-sm text-neutral-500">
            {generalInformationFormData.email}
          </p>
          <p className="rounded-full bg-warning-50 px-3 py-1 text-warning-950">
            Transferred needed
          </p>
        </div>
      )}
    </div>
  );
};
